import { type IconProps, PencilSimpleLineIcon } from "@phosphor-icons/react";
import type * as React from "react";
import { cloneElement, isValidElement } from "react";
import { cn } from "@/lib/utils";

type CardSize = "default" | "sm" | "xs";

interface CardBaseProps {
  size?: CardSize;
  className?: string;
}

type CardProps =
  | (CardBaseProps &
      Omit<React.ComponentProps<"div">, keyof CardBaseProps> & { as?: "div" })
  | (CardBaseProps &
      Omit<React.ComponentProps<"button">, keyof CardBaseProps> & {
        as: "button";
      });

const cardClassName = () =>
  cn(
    "group/card flex flex-col gap-4 overflow-hidden rounded-xl bg-card py-4 text-sm text-card-foreground ring-1 ring-card-border",
    "has-data-[slot=card-footer]:pb-0 has-[>img:first-child]:pt-0",
    "*:[img:first-child]:rounded-t-xl *:[img:last-child]:rounded-b-xl",
    "data-[size=sm]:gap-3 data-[size=sm]:py-3 data-[size=sm]:text-xs data-[size=sm]:has-data-[slot=card-footer]:pb-0",
    "data-[size=xs]:max-w-[208px] data-[size=xs]:gap-2 data-[size=xs]:py-2 data-[size=xs]:text-xxs data-[size=xs]:has-data-[slot=card-footer]:pb-0",
  );

const Card = ({
  className,
  size = "default",
  as,
  children,
  ...props
}: CardProps) => {
  const classes = cn(cardClassName(), className);
  if (as === "button") {
    const buttonProps = props as React.ComponentProps<"button">;
    return (
      <button
        type="button"
        data-slot="card"
        data-size={size}
        className={classes}
        {...buttonProps}
      >
        {children}
      </button>
    );
  }
  return (
    <div
      data-slot="card"
      data-size={size}
      className={classes}
      {...(props as React.ComponentProps<"div">)}
    >
      {children}
    </div>
  );
};

const CardHeader = ({ className, ...props }: React.ComponentProps<"div">) => (
  <div
    data-slot="card-header"
    className={cn(
      "group/card-header @container/card-header grid auto-rows-min items-start gap-1 rounded-t-xl px-4",
      "has-data-[slot=card-action]:grid-cols-[1fr_auto] has-data-[slot=card-description]:grid-rows-[auto_auto]",
      "[.border-b]:pb-4 group-data-[size=sm]/card:[.border-b]:pb-3 group-data-[size=xs]/card:[.border-b]:pb-2",
      "group-data-[size=sm]/card:px-3 group-data-[size=xs]/card:px-2",
      className,
    )}
    {...props}
  />
);

const CardTitle = ({ className, ...props }: React.ComponentProps<"div">) => (
  <div
    data-slot="card-title"
    className={cn(
      "text-base leading-snug font-medium",
      "group-data-[size=sm]/card:text-xs group-data-[size=xs]/card:text-xxs",
      className,
    )}
    {...props}
  />
);

const CardDescription = ({
  className,
  ...props
}: React.ComponentProps<"div">) => (
  <div
    data-slot="card-description"
    className={cn(
      "text-sm text-muted-foreground",
      "group-data-[size=sm]/card:text-xs group-data-[size=xs]/card:text-xxs",
      className,
    )}
    {...props}
  />
);

const CardAction = ({ className, ...props }: React.ComponentProps<"div">) => (
  <div
    data-slot="card-action"
    className={cn(
      "font-normal",
      "col-start-2 row-span-2 row-start-1 self-start justify-self-end text-sm",
      "group-data-[size=sm]/card:text-xs group-data-[size=xs]/card:text-xxs",
      className,
    )}
    {...props}
  />
);

const CardContent = ({ className, ...props }: React.ComponentProps<"div">) => (
  <div
    data-slot="card-content"
    className={cn(
      "px-4 text-sm",
      "group-data-[size=sm]/card:px-3 group-data-[size=sm]/card:text-xs",
      "group-data-[size=xs]/card:px-2 group-data-[size=xs]/card:text-xxs",
      className,
    )}
    {...props}
  />
);

const CardFooter = ({ className, ...props }: React.ComponentProps<"div">) => (
  <div
    data-slot="card-footer"
    className={cn(
      "flex items-center rounded-b-xl border-t bg-muted/50 p-4 text-sm",
      "group-data-[size=sm]/card:p-3 group-data-[size=sm]/card:text-xs",
      "group-data-[size=xs]/card:p-2 group-data-[size=xs]/card:text-xxs",
      className,
    )}
    {...props}
  />
);

/** Props for {@link PromptCard}. */
export interface PromptCardProps
  extends Omit<React.ComponentProps<"button">, "children"> {
  /** `mono`: Figma "Prompt suggestion chips" — neutral bordered chip, shadow on hover, gray when pressed. */
  variant?: "default" | "mono";
  /** Main prompt text shown in the card. */
  prompt: string;
  /** Label pill text (e.g. "First Drafts"). Defaults to "First Drafts". */
  label?: string;
  /**
   * Icon element shown in the label pill (e.g. from Phosphor).
   * @example icon={<PencilSimpleLineIcon />}
   */
  icon?: React.ReactElement<IconProps>;
  /** Class names for the label text and icon (e.g. "text-gray-800"). Applied to both. Defaults to "text-gray-800". */
  labelClassNames?: string;
  /** Class names for the label pill container (e.g. "bg-gray-100"). Defaults to "bg-gray-100". */
  labelPillClassNames?: string;
  /** Optional class name for the card root. */
  className?: string;
}

const DEFAULT_LABEL_CLASS_NAMES = "text-gray-800";
const DEFAULT_LABEL_PILL_CLASS_NAMES = "bg-gray-100";

/** Prompt text plus optional label pill with icon; renders as a button. */
const PromptCard = ({
  variant = "default",
  prompt,
  label = "First Drafts",
  icon,
  labelClassNames = DEFAULT_LABEL_CLASS_NAMES,
  labelPillClassNames = DEFAULT_LABEL_PILL_CLASS_NAMES,
  className,
  ...buttonProps
}: PromptCardProps) => {
  const isMono = variant === "mono";

  const chipIconAndLabelClassNames = cn(
    labelClassNames,
    isMono && "text-gray-500",
  );
  const labelIconClass = cn(
    isMono ? "size-3" : "size-2.5",
    "shrink-0",
    chipIconAndLabelClassNames,
  );
  const defaultIconSize = isMono ? 12 : 10;
  const labelIcon =
    icon && isValidElement(icon) ? (
      cloneElement(icon as React.ReactElement<IconProps>, {
        className: cn((icon.props as IconProps).className, labelIconClass),
        size: (icon.props as IconProps).size ?? defaultIconSize,
      })
    ) : (
      <PencilSimpleLineIcon className={labelIconClass} size={defaultIconSize} />
    );

  const cardShellClassName = cn(
    "w-full cursor-pointer rounded border border-gray-200 bg-white py-0 text-left ring-0 transition-colors hover:bg-gray-50 focus-visible:outline focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    isMono &&
      "gap-[9px] border-[#e4e4e7] transition-[background-color,box-shadow] hover:bg-white hover:shadow-[0_0_8px_0_rgba(0,0,0,0.08)] active:bg-[#fafafa]",
    className,
  );

  const contentPaddingClassName = isMono
    ? "px-3.5 pt-3 pb-0"
    : "px-3 pt-2.5 pb-2";

  const footerPaddingClassName = isMono ? "px-3.5 pb-[11px]" : "px-3 pb-2.5";

  const promptClassName = cn(
    "leading-4",
    isMono
      ? "min-h-[50px] text-[13px] font-normal tracking-normal text-gray-700"
      : "tracking-[0.12px] text-gray-800",
  );

  const chipShellClassName = cn(
    "inline-flex w-fit max-w-full items-center gap-1 rounded-md px-2 py-1",
    labelPillClassNames,
    isMono &&
      "border border-[#e4e4e7] bg-white transition-colors group-active/card:bg-[#fafafa]",
  );

  const chipLabelClassName = cn(
    "overflow-hidden text-ellipsis whitespace-nowrap text-[10px] leading-4",
    isMono ? "font-medium tracking-normal" : "tracking-[0.1px]",
    chipIconAndLabelClassNames,
  );

  return (
    <Card
      as="button"
      size="default"
      data-variant={variant}
      className={cardShellClassName}
      {...buttonProps}
    >
      <CardContent
        className={cn("min-h-0 flex-1 text-xs", contentPaddingClassName)}
      >
        <p className={promptClassName}>{prompt}</p>
      </CardContent>
      <CardFooter
        className={cn("border-t-0 bg-transparent pt-0", footerPaddingClassName)}
        aria-hidden
      >
        <div className={chipShellClassName}>
          {labelIcon}
          <span className={chipLabelClassName}>{label}</span>
        </div>
      </CardFooter>
    </Card>
  );
};

export {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  PromptCard,
};
